<template>
  <div class="mt-4">
    <v-flex
      d-flex
      align-center
      justify-space-between
      class="mb-4 px-4"
      v-if="devicesConnectivity[0]"
    >
      <div class="body-2 font-weight-medium">
        {{ $t("modules.admin.usageMonitoring.environmentName") }} :
        {{ devicesConnectivity[0].environment_name }}
      </div>
      <div class="body-2 font-weight-medium">
        {{ $t("modules.admin.usageMonitoring.currentWeek") }} :
        {{ devicesConnectivity[0].current_week }}
        {{ $t("modules.admin.usageMonitoring.hrs") }}
      </div>
      <div class="body-2 font-weight-medium">
        {{ $t("modules.admin.usageMonitoring.currentMonth") }} :
        {{ devicesConnectivity[0].current_month }}
        {{ $t("modules.admin.usageMonitoring.hrs") }}
      </div>
      <div class="body-2 font-weight-medium">
        {{ $t("modules.admin.usageMonitoring.currentYear") }} :
        {{ devicesConnectivity[0].current_year }}
        {{ $t("modules.admin.usageMonitoring.hrs") }}
      </div>
      <v-btn
        small
        color="primary"
        outlined
        @click="downloadCSV"
        :loading="isDownloading"
        >{{ $t("actions.exportCSV") }}
      </v-btn>
    </v-flex>
    <v-flex d-flex align-baseline class="my-2">
      <v-subheader class="font-weight-bold">
        <span id="versions-management__rows">
          <span id="versions-management__total-string">Total: </span>
          <span id="versions-management__rows-count">{{ rowsCount }}</span>
        </span>
      </v-subheader>
      <v-text-field
        v-model="search"
        dense
        class="ml-4"
        height="31"
        style="width: 50px"
        :label="$t('actions.search')"
        prepend-icon="mdi-magnify"
        hide-details
        clearable
        single-line
      ></v-text-field>
      <v-spacer></v-spacer>
    </v-flex>
    <v-subheader class="font-weight-bold">
      <v-breadcrumbs :items="breadcrumbs" large class="pa-0">
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item
            :href="item.href"
            :disabled="item.environment_id === environment_id"
            @click.prevent="removeBreadcrumbs(item.id, item.environment_id)"
          >
            {{ item.text }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
    </v-subheader>
    <v-data-table
      ref="dataContainer"
      :headers="headers"
      :items="loadItems"
      :loading="loading"
      class="elevation-1 mx-2 mt-3 scroll row-cursor"
      :options.sync="options"
      hide-default-footer
      style="min-height: 100%;"
      id="usage-monitoring__table"
      fixed-header
      must-sort
      :height="height - 15 + 'px'"
      @dblclick:row="selectRow"
    >
      <template v-slot:body.append>
        <td
          colspan="3"
          class="text-center py-2"
          v-if="options.itemsPerPage < loadItems.length"
        >
          <v-progress-circular
            :size="30"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </td>
      </template>
      <template v-slot:item.current_week="{ item }">
        <span
          >{{ item.current_week }}
          {{ $t("modules.admin.usageMonitoring.hrs") }}</span
        >
      </template>
      <template v-slot:item.current_month="{ item }">
        <span
          >{{ item.current_month }}
          {{ $t("modules.admin.usageMonitoring.hrs") }}</span
        >
      </template>
      <template v-slot:item.current_year="{ item }">
        <span
          >{{ item.current_year }}
          {{ $t("modules.admin.usageMonitoring.hrs") }}</span
        >
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { handleError } from "@/utils/error";
import util from "@/modules/admin/usage-monitoring/util";
import { logMixins } from "@/modules/logs/mixins";
import { mapState } from "vuex";
import { getDeepCopy } from "@/utils";

const FILENAME = "usage_monitoring";

export default {
  mixins: [logMixins],
  data() {
    return {
      breadcrumbs: [],
      headers: [
        {
          text: this.$t("modules.admin.usageMonitoring.subEnvironmentName"),
          align: "start",
          value: "environment_name"
        },
        {
          text: this.$t("modules.admin.usageMonitoring.currentWeek"),
          align: "center",
          value: "current_week"
        },
        {
          text: this.$t("modules.admin.usageMonitoring.currentMonth"),
          align: "center",
          value: "current_month"
        },
        {
          text: this.$t("modules.admin.usageMonitoring.currentYear"),
          align: "center",
          value: "current_year"
        }
      ],
      height: 0,
      rows: 20,
      options: {},
      loading: false,
      loadItems: [],
      util,
      isDownloading: false,
      environment_id: null,
      params: {
        sub_environment_id: null
      }
    };
  },
  computed: {
    ...mapState("login", ["user"]),
    devicesConnectivity() {
      return this.$store.state.devicesConnectivity.devices_connectivity;
    },
    getSubEnvs() {
      let subDevicesConnectivity = [];
      subDevicesConnectivity.push(...this.devicesConnectivity);
      subDevicesConnectivity.shift();
      return subDevicesConnectivity;
    },
    search: {
      get() {
        return this.$store.state.devicesConnectivity.search || "";
      },
      set(val) {
        this.$store.commit("devicesConnectivity/updateSearch", val);
      }
    },
    rowsCount() {
      return this.loadItems.length;
    }
  },
  created() {
    this.breadcrumbs.push({
      text: this.user.environment_name,
      href: "/admin/usage-monitoring",
      id: 0,
      disabled: false,
      environment_id: null
    });
    if (this.$route.params.id) {
      this.$router.push({
        name: "usageMonitoring"
      });
    }
  },
  methods: {
    initialize() {
      this.options.itemsPerPage = this.rows;
      this.loading = true;
      this.$store
        .dispatch("devicesConnectivity/fetchDevicesConnectivity", this.params)
        .then(() => {
          if (this.search) {
            this.searchItems(this.search);
          } else {
            this.loadItems = this.getSubEnvs;
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchItems(val) {
      this.loadItems = this.getSubEnvs.filter(o =>
        this.headers
          .map(item => item.value)
          .some(k => {
            if (o[k]) {
              return (
                o[k]
                  .toString()
                  .toLowerCase()
                  .indexOf(val.toLowerCase()) > -1
              );
            }
          })
      );
    },
    getBreadcrumbs(val) {
      this.getSubEnvs.find(item => {
        if (item.environment_id === val) {
          this.breadcrumbs.push({
            text: item.environment_name,
            href: `/admin/usage-monitoring/${item.environment_id}`,
            environment_id: item.environment_id,
            id:
              this.breadcrumbs.reduce(
                (max, item) => (item.id > max ? item.id : max),
                0
              ) + 1
          });
        }
      });
    },
    removeBreadcrumbs(breadcrumbId, env_id) {
      if (this.environment_id === env_id) return;
      this.breadcrumbs = this.breadcrumbs.filter(
        item => item.id <= breadcrumbId
      );
      this.environment_id = env_id;
      this.$router.push({
        name: "usageMonitoring",
        params: { id: this.environment_id }
      });
      this.params = {
        sub_environment_id: this.environment_id
      };
      this.initialize();
    },
    downloadCSV() {
      this.isDownloading = true;
      let data = [];

      if (this.search) {
        this.searchItems(this.search);
        data = getDeepCopy(this.loadItems);
      } else {
        data = getDeepCopy(this.devicesConnectivity);
      }

      const formattedData = data.map(item => {
        const formattedDataItem = {
          environment_name: item.environment_name,
          current_week: item.current_week,
          current_month: item.current_month,
          current_year: item.current_year
        };
        return formattedDataItem;
      });

      this.exportCSVFile(util.csvHeaders, formattedData, FILENAME);
      this.isDownloading = false;
    },
    scroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (this.options.itemsPerPage < this.devicesConnectivity.length) {
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          this.paginate();
        }
      }
    },
    computeHeight() {
      let el = this.$refs.dataContainer.$el;
      let boxOffset = el.getBoundingClientRect();
      let viewPortHeight = window.innerHeight;
      this.height = viewPortHeight - boxOffset.top;
    },
    paginate() {
      this.options.itemsPerPage = this.options.itemsPerPage + this.rows;
    },
    selectRow(event, { item }) {
      this.environment_id = item.environment_id;
      this.$router.push({
        name: "usageMonitoring",
        params: { id: item.environment_id }
      });
      this.params = {
        sub_environment_id: item.environment_id
      };
      this.initialize();
    }
  },
  watch: {
    search(val) {
      this.searchItems(val);
    },
    getSubEnvs() {
      if (!this.search) {
        this.loadItems = this.getSubEnvs;
      } else {
        this.searchItems(this.search);
      }
    },
    "$route.params.id": {
      immediate: true,
      handler(val) {
        if (val) {
          this.getBreadcrumbs(val);
        }
      }
    }
  },
  beforeDestroy() {
    this.search = "";
    document
      .querySelector("#usage-monitoring__table .v-data-table__wrapper")
      .removeEventListener("scroll", this.scroll);
  },
  mounted() {
    let el = document.querySelector(
      "#usage-monitoring__table .v-data-table__wrapper"
    );
    el.addEventListener("scroll", this.scroll);
    this.computeHeight();
  }
};
</script>

<style scoped>
.row-cursor >>> tbody tr :hover {
  cursor: pointer;
}
</style>
